import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { get } from 'lodash-es';
import { Button, Container } from 'reactstrap';
import ReactLoading from 'react-loading';

import AddLotDropzone from './AddLotDropzone';
import UnusedLotData from './UnusedLotData';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import validateLotForm from '../diy-utils/validateLotForm';
import LotForm from '../diy-forms/LotForm';

const initialLotData = {
  artistName: '',
  circa: '',
  condition: '',
  dimensions: '',
  exhibited: '',
  hiEst: '',
  invNumber: '',
  itemPrice: '',
  literature: '',
  loEst: '',
  lotDescription: '',
  lotExt: '',
  lotNumber: '',
  lotTitle: '',
  medium: '',
  notes: '',
  provenance: '',
  reserve: '',
  shipHeight: '',
  shipLength: '',
  shipWeight: '',
  shipWidth: '',
  shipping: '',
  shippingIntl: '',
  startingBid: ''
};

const initialValidation = {
  lotNumber: null,
  lotExt: null,
  lotTitle: null,
  lotDescription: null,
  shipWidth: null,
  shipHeight: null,
  shipLength: null,
  shipWeight: null,
  shipping: null,
  shippingIntl: null,
  loEst: null,
  hiEst: null,
  startingBid: null,
  invNumber: null,
  reserve: null
};

const getStepTrackerInfo = (linkedFrom, isBin) => {
  let stepName = '';
  let stepNumber = '';
  if (isBin) {
    stepName = linkedFrom === 'step4' ? 'Preview' : 'Summary';
    stepNumber = linkedFrom === 'step4' ? 4 : 5;
  } else {
    stepName = linkedFrom === 'step5' ? 'Preview' : 'Summary';
    stepNumber = linkedFrom === 'step5' ? 5 : 6;
  }

  return { stepName, stepNumber };
};

const AddLot = ({ history, location, match }) => {
  const house = useSelector(state => state.house);
  const policy = useSelector(state => state.policy);

  /**
   * Add a reference in the JSX we can scroll to when the user submits add lot.
   *
   * Essentially the dropzone needs to remain hidden until after the user submits lot information.
   * This allows us to transgress into the 'edit lot' permutation from the 'add lot' permutation.
   */
  const myRef = useRef(null);

  const [error, setError] = useState(null);
  const [errorDeletedLot, setErrorDeletedLot] = useState(false);
  const [invNumberArray, setInvNumberArray] = useState([]);
  const [lotNumberArray, setLotNumberArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lotData, setLotData] = useState(initialLotData);
  const [lotImages, setLotImages] = useState([]);
  const [orderBids, setOrderBids] = useState([]);
  const [deletedLotsArray, setDeletedLotsArray] = useState([]);
  const [showDeletedLotError, setShowDeletedLotError] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const [validation, setValidation] = useState(initialValidation);

  const {
    params: { catalogID, groupID, lotNumber, type, goToPage }
  } = match;

  useEffect(() => {
    const lotIdParam = lotNumber;

    (async () => {
      try {
        setLoading(true);

        const isEditing = lotIdParam !== '0';

        if (isEditing) {
          // Re-sequence all the images first in case the user uploaded them as like 1, 5, 25
          try {
            await axios(`/cps/app/catalog/${catalogID}/${lotIdParam}/resequenceimages`);
          } catch (err) {
            setError(err);
            setLoading(false);
          }
        }

        /**
         * Preview API should be faster than getting all the extra crap from the other lots, since
         * we only need the lot number list and inventory number list
         */
        const requestChain = [
          axios(`/cps/app/catalog/${catalogID}/orderbids`),
          axios(`/cps/app/catalog/${catalogID}/preview`),
          axios(`/cps/app/catalog/${catalogID}/deletedlots`)
        ];

        // In the edit session - get all the lot details
        if (isEditing) {
          requestChain.push(axios(`/cps/app/catalog/${catalogID}/${lotIdParam}`));
          requestChain.push(
            axios(`/cps/app/catalog/${catalogID}/${lotIdParam}/house/${house.houseID}/getimageurls`)
          );
        }

        const [
          orderBidResponse,
          lotResponse,
          deletedLotResponse,
          lotDataResponse,
          lotImageResponse
        ] = await Promise.all(requestChain);

        const nextOrderBids = get(orderBidResponse, 'data.data.orderbids', null);
        const lots = get(lotResponse, 'data.data.lots', null);
        const nextDeletedLotsArray = get(deletedLotResponse, 'data.data.deletedLotsArray', []);
        const nextLotNumberArray = lots.map(lot => lot.lotNumber + lot.lotExt);
        const nextInvNumberArray = lots.map(lot => lot.invNumber);

        if (lotDataResponse) {
          const nextLotData = get(lotDataResponse, 'data.data.lots[0]', []);
          const { lotNumber: dataLotNumber, lotExt, invNumber, lotID } = nextLotData;
          const lotNumberExt = `${dataLotNumber + lotExt}`;
          nextLotData.hasLotNumber = dataLotNumber;
          const nextLotImages = get(lotImageResponse, `data.data[${lotID}]`, []);

          /**
           * Remove the existing inventory number / lot number from the array in case the user is
           * editing and wants to keep their existing lot number / inventory number.
           */
          const lotNumberIndex = nextLotNumberArray.findIndex(lotNum => lotNum === lotNumberExt);
          const invNumberIndex = nextInvNumberArray.findIndex(invNum => invNum === invNumber);
          nextLotNumberArray.splice(lotNumberIndex, 1);
          nextInvNumberArray.splice(invNumberIndex, 1);

          setDeletedLotsArray(nextDeletedLotsArray);
          setInvNumberArray(nextInvNumberArray);
          setLotData(nextLotData);
          setLotImages(nextLotImages);
          setLotNumberArray(nextLotNumberArray);
          setOrderBids(nextOrderBids);
          setLoading(false);
        } else {
          setDeletedLotsArray(nextDeletedLotsArray);
          setOrderBids(nextOrderBids);
          setInvNumberArray(nextInvNumberArray);
          setLotNumberArray(nextLotNumberArray);
          setLoading(false);
        }
      } catch (err) {
        const errorURL = err.request.responseURL;
        if (errorURL.indexOf('getimageurls') >= 0) {
          setErrorDeletedLot(true);
          setLoading(false);
        } else {
          setError(err);
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dynamically set changed input fields in the state
  const handleInputChange = e => {
    const { id, value } = e.target;

    setValidation(prev => {
      const next = { ...prev };
      // Clear validation until the user exits the field
      delete next[id];
      return next;
    });

    setLotData(prev => ({ ...prev, [id]: value }));
  };

  const submitLot = async () => {
    const isBin = type.toLowerCase() === 'bin';
    const isAddingLot = lotNumber === '0';

    // Add BIN property
    lotData.isBinLot = isBin;

    // UnusedLotData needs to be appended if we are adding a lot or else CPS gets angry
    const postData = { ...lotData, ...UnusedLotData };

    // CPS needs BIN value as itemPrice - even though it may return it to us as startingBid
    if (isBin) {
      delete postData.startingBid;
    } else {
      // FE workaround to handle weird CPS behavior which changes startingBid values for some reason
      delete postData.itemPrice;
    }

    try {
      const addLotPost = await axios.post(
        `/cps/app/catalog/${catalogID}/lot/${isAddingLot ? 'new' : 'update'}`,
        postData,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      // only if Invavluable Shipping is enabled
      if (policy?.id) {
        try {
          await axios.post(
            `/cps/app/catalog/${catalogID}/shipping-review`,
            isAddingLot ? {} : { lotIDs: [lotNumber] },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (err) {
          console.log('Shipping review error update failed with ', err); // eslint-disable-line no-console
        }
      }
      const baseUrl = `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}`;
      if (isAddingLot) {
        const lotID = addLotPost.data.data.lots[0].lotID;
        history.push(`${baseUrl}/lot/${lotID}`);

        // Bring user to the upload images dropzone
        window.scrollTo({
          top: myRef.current.offsetTop,
          behavior: 'smooth'
        });
      } else {
        const linkedFrom = get(location, 'state.linkedFrom', null);
        const { stepNumber } = getStepTrackerInfo(linkedFrom, isBin);
        const redirectPage = goToPage ? `/${goToPage}` : '';
        history.push(`${baseUrl}/${stepNumber}${redirectPage}`);
      }
    } catch (err) {
      window.scrollTo(0, 0);
      const respErrors = get(err, 'response.data.errors', []);
      const hasDeletedLotError = respErrors.filter(e => e.errorCode === 112).length > 0;
      if (hasDeletedLotError) {
        setShowDeletedLotError(true);
      } else {
        setShowSubmitError(true);
      }
    }
  };

  const validateFields = fieldName => {
    // validate user missing the correct connectType (platinum (e) or gold (p))
    const ycConnectTypes = ['e', 'p'];
    const isGoldPlatinum = ycConnectTypes.indexOf(house.connectType) !== -1;

    setValidation(
      validateLotForm(
        new Set(deletedLotsArray),
        fieldName,
        invNumberArray,
        lotData,
        lotNumberArray,
        type,
        validation,
        isGoldPlatinum
      )
    );
  };

  // validate user missing the correct connectType (platinum (e) or gold (p))
  const ycConnectTypes = ['e', 'p'];
  const isGoldPlatinum = ycConnectTypes.indexOf(house.connectType) !== -1;

  if (!catalogID || error) {
    return (
      <Container className="py-4">
        <InlineAlert />
      </Container>
    );
  }

  if (loading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  const linkedFrom = get(location, 'state.linkedFrom', null);
  const isBin = type.toLowerCase() === 'bin';

  const { stepName, stepNumber } = getStepTrackerInfo(linkedFrom, isBin);

  if (errorDeletedLot) {
    return (
      <Container className="py-4">
        <InlineAlert
          titleText="Lot Deleted"
          baseText="This lot has been deleted and can no longer be edited"
          showPhones={false}
        />
        <Link
          to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`}
        >
          {`Click Here to Return to ${stepName}`}
        </Link>
      </Container>
    );
  }

  const isEditing = lotNumber !== '0';
  const addOrEditText = isEditing ? 'Edit' : 'Add';

  /**
   * Disable the save button if there are validation errors, or if the user is missing required
   * fields
   */
  const { lotDescription, lotNumber: lotNumberFromForm, lotTitle } = lotData;
  const lotN = isGoldPlatinum ? false : !lotNumberFromForm;
  let missingRequiredFields = !lotDescription || lotN || !lotTitle;

  if (isBin) {
    const { itemPrice } = lotData;
    missingRequiredFields = missingRequiredFields || !itemPrice;
  }

  const disableSubmit =
    Object.keys(validation).some(key => validation[key]) || missingRequiredFields;

  const hasOrderBids = orderBids.some(item => item === lotData.lotNumber + lotData.lotExt);

  return (
    <Container className="py-4">
      <h1 className="mb-1">{addOrEditText} Lot</h1>
      <p>* = Required</p>

      {showDeletedLotError && (
        <InlineAlert
          titleText="Previously Deleted Lot Number"
          baseText={`Lot Number ${lotNumberFromForm}${lotData.lotExt} was used on a previously deleted lot.  Please enter a new lot number.`}
          showPhones={false}
        />
      )}

      {showSubmitError && (
        <InlineAlert
          baseText={`Unable to ${addOrEditText.toLowerCase()} your lot. Please try again later or contact Customer Care any time at:`}
        />
      )}

      <LotForm
        handleInputChange={handleInputChange}
        hasOrderBids={hasOrderBids}
        isEditing={isEditing}
        lotData={lotData}
        type={type}
        validateFields={validateFields}
        validation={validation}
      />

      {/* Needed in order to use a scroll to when submitting Add Lot */}
      <div className="mb-3" ref={myRef} />
      {isEditing && lotData && (
        <AddLotDropzone houseID={house.houseID} lotImages={lotImages} lotData={lotData} />
      )}

      <div className="mt-4">
        <Button
          className={`${disableSubmit ? 'cursor-not-allowed' : ''} min-width-max-content`}
          color="primary"
          disabled={disableSubmit}
          role="link"
          onClick={() => submitLot()}
        >
          {!isEditing ? 'Save & Add Images' : `Save & Return to ${stepName}`}
        </Button>

        <Link
          className="ml-3"
          to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`}
        >
          {`Cancel & Return to ${stepName}`}
        </Link>
      </div>
    </Container>
  );
};

export default withRouter(AddLot);
